// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { useState } from "react";

const MEETING_NOTES = [
  { id:1, title:"Weekly Team Standup - Feb 7", date:"2026-02-07", category:"Weekly Standup", attendees:["Ryan Miskulin","Jordan Lutz","Taylor Schenher","Lucas Mason","Tyler Hodgson"],
    content:"Reviewed sprint progress. Platform notifications at 70% completion. Q1 revenue model in final review. Canstorr partnership agreement progressing - legal review next week. Security pen test scheduled for Feb 17. Action items assigned for deployment pipeline updates.",
    actionItems:[
      { text:"Complete notification system testing", assignee:"Jordan Lutz", status:"in-progress" },
      { text:"Finalize Q1 revenue forecast", assignee:"Taylor Schenher", status:"completed" },
      { text:"Schedule Canstorr legal review call", assignee:"Lucas Mason", status:"pending" },
      { text:"Prepare pen test scope document", assignee:"Tyler Hodgson", status:"pending" }
    ]},
  { id:2, title:"Client Meeting - Canstorr Phase 2", date:"2026-02-05", category:"Client Meeting", attendees:["Ryan Miskulin","Tyson Becker","Lucas Mason"],
    content:"Met with Canstorr leadership to discuss Phase 2 expansion into western Canada. Storage capacity to increase to 30,000 MT. CropPatrol integration on schedule. Discussed pricing model for premium features. Tyson to coordinate site visit for late February.",
    actionItems:[
      { text:"Send revised SOW to Canstorr", assignee:"Lucas Mason", status:"in-progress" },
      { text:"Prepare Phase 2 pricing proposal", assignee:"Ryan Miskulin", status:"pending" },
      { text:"Coordinate site visit schedule", assignee:"Tyson Becker", status:"pending" }
    ]},
  { id:3, title:"Board Meeting - Q4 Review", date:"2026-02-03", category:"Board Meeting", attendees:["Ryan Miskulin","Taylor Schenher","Clorice Robertson"],
    content:"Presented Q4 financials to the board. Revenue up 23% QoQ. Customer acquisition cost reduced by 15%. Approved budget for Q1 hiring plan including two new developers. Discussed market expansion strategy for mining and agriculture verticals.",
    actionItems:[
      { text:"Distribute board meeting minutes", assignee:"Clorice Robertson", status:"completed" },
      { text:"Draft Q1 hiring plan", assignee:"Ryan Miskulin", status:"in-progress" },
      { text:"Prepare market expansion report", assignee:"Lee Mehling", status:"pending" }
    ]},
  { id:4, title:"Product Review - VitaSync Features", date:"2026-02-04", category:"Product Review", attendees:["Jordan Lutz","Des Bachynski","Tyler Hodgson"],
    content:"Reviewed VitaSync beta feature set. Health tracking dashboard performing well in pilot clinics. Compliance module needs additional regulatory updates for 2026. Discussed integration with provincial health databases. Beta feedback from 3 clinics is positive overall.",
    actionItems:[
      { text:"Update compliance documentation", assignee:"Des Bachynski", status:"pending" },
      { text:"Fix performance issue on health dashboard", assignee:"Jordan Lutz", status:"in-progress" },
      { text:"Compile beta feedback report", assignee:"Tyler Hodgson", status:"completed" }
    ]},
  { id:5, title:"Strategy Session - Q1 Priorities", date:"2026-02-01", category:"Strategy", attendees:["Ryan Miskulin","Taylor Schenher","Lucas Mason","Jordan Lutz"],
    content:"Defined Q1 strategic priorities. Focus areas: platform stability, new customer acquisition, mining vertical expansion. Sales target set at $150K MRR by end of Q1. Development to prioritize notification system and API v3. Marketing budget approved for industry conference attendance.",
    actionItems:[
      { text:"Create Q1 sales roadmap", assignee:"Lucas Mason", status:"completed" },
      { text:"Define API v3 specification", assignee:"Jordan Lutz", status:"in-progress" },
      { text:"Book industry conference booths", assignee:"Lucas Mason", status:"pending" },
      { text:"Finalize Q1 marketing budget allocation", assignee:"Taylor Schenher", status:"completed" }
    ]},
  { id:6, title:"Weekly Team Standup - Jan 31", date:"2026-01-31", category:"Weekly Standup", attendees:["Ryan Miskulin","Jordan Lutz","Taylor Schenher","Des Bachynski","Tyler Hodgson","Lucas Mason"],
    content:"End-of-month standup. Reviewed January milestones. 12 of 15 sprint tasks completed. AR reconciliation completed on time. Insurance broker leads campaign launched. Discussed February priorities and sprint planning for next week.",
    actionItems:[
      { text:"Complete remaining 3 sprint tasks", assignee:"Jordan Lutz", status:"completed" },
      { text:"Submit January expense reports", assignee:"Clorice Robertson", status:"completed" },
      { text:"Prepare February sprint backlog", assignee:"Tyler Hodgson", status:"completed" }
    ]}
];

const CATEGORIES = ["All", "Weekly Standup", "Client Meeting", "Board Meeting", "Product Review", "Strategy"];

export default function MeetingNotesPage() {
  const [notes] = useState(MEETING_NOTES);
  const [selectedNote, setSelectedNote] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");

  const filtered = notes.filter(n => {
    if (categoryFilter !== "All" && n.category !== categoryFilter) return false;
    if (searchTerm && !n.title.toLowerCase().includes(searchTerm.toLowerCase()) && !n.content.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const totalActions = notes.reduce((sum, n) => sum + n.actionItems.length, 0);
  const completedActions = notes.reduce((sum, n) => sum + n.actionItems.filter(a => a.status === "completed").length, 0);
  const pendingActions = notes.reduce((sum, n) => sum + n.actionItems.filter(a => a.status === "pending").length, 0);

  const statusBadge = (status) => {
    const map = { completed:"badge badge-green", "in-progress":"badge badge-yellow", pending:"badge badge-red" };
    return map[status] || "badge";
  };

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Meeting Notes</h1><p>{notes.length} meetings documented</p></div>
        <button className="btn btn-primary">+ New Meeting Note</button>
      </div>

      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">Total Meetings</div><div className="stat-value">{notes.length}</div></div>
        <div className="stat-card"><div className="stat-label">Action Items</div><div className="stat-value">{totalActions}</div></div>
        <div className="stat-card"><div className="stat-label">Completed Actions</div><div className="stat-value" style={{color:"#10b981"}}>{completedActions}</div></div>
        <div className="stat-card"><div className="stat-label">Pending Actions</div><div className="stat-value" style={{color:"#ef4444"}}>{pendingActions}</div></div>
      </div>

      <div style={{display:"flex",gap:12,marginBottom:16}}>
        <input className="input" placeholder="Search meeting notes..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{flex:1}} />
        <select className="input" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} style={{width:200}}>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="grid-2">
        <div>
          {filtered.map(note => (
            <div key={note.id} className="card" style={{cursor:"pointer",border:selectedNote?.id===note.id?"1px solid #10b981":"1px solid transparent"}} onClick={() => setSelectedNote(note)}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                <div>
                  <div style={{fontSize:14,fontWeight:600,color:"#e2e8f0"}}>{note.title}</div>
                  <div style={{fontSize:12,color:"#64748b",marginTop:2}}>{note.date}</div>
                </div>
                <span className="badge badge-blue">{note.category}</span>
              </div>
              <div style={{fontSize:12,color:"#94a3b8",display:"flex",gap:12}}>
                <span>{note.attendees.length} attendees</span>
                <span>{note.actionItems.length} action items</span>
                <span style={{color:"#10b981"}}>{note.actionItems.filter(a=>a.status==="completed").length} done</span>
              </div>
            </div>
          ))}
        </div>

        <div>
          {selectedNote ? (
            <div className="card">
              <div style={{marginBottom:16}}>
                <h2 style={{fontSize:18,fontWeight:700,color:"#e2e8f0",marginBottom:4}}>{selectedNote.title}</h2>
                <div style={{display:"flex",gap:12,fontSize:12,color:"#64748b"}}>
                  <span>{selectedNote.date}</span>
                  <span className="badge badge-blue">{selectedNote.category}</span>
                </div>
              </div>

              <div style={{marginBottom:16}}>
                <h4 style={{fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:6}}>Attendees</h4>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {selectedNote.attendees.map(a => <span key={a} className="badge badge-purple">{a}</span>)}
                </div>
              </div>

              <div style={{marginBottom:16}}>
                <h4 style={{fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:6}}>Notes</h4>
                <p style={{fontSize:13,color:"#e2e8f0",lineHeight:1.7}}>{selectedNote.content}</p>
              </div>

              <div>
                <h4 style={{fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:8}}>Action Items ({selectedNote.actionItems.length})</h4>
                {selectedNote.actionItems.map((item, idx) => (
                  <div key={idx} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:"1px solid #334155"}}>
                    <div>
                      <div style={{fontSize:13,color:"#e2e8f0"}}>{item.text}</div>
                      <div style={{fontSize:11,color:"#64748b"}}>Assigned to: {item.assignee}</div>
                    </div>
                    <span className={statusBadge(item.status)}>{item.status}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="card" style={{textAlign:"center",padding:40,color:"#64748b"}}>
              <p>Select a meeting note to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
